import React, { useState, ComponentType, useCallback } from 'react';

import { FormattedMessage, InjectedIntl, injectIntl } from 'react-intl';

import { PrimaryDropdownButton } from '@atlaskit/atlassian-navigation';
import InlineDialog from '@atlaskit/inline-dialog';
import { PopupMenuGroup, Section } from '@atlaskit/menu';
import { Popup } from '@atlaskit/popup';

import { AnalyticsEvent, MenuItem } from '../../types';

import messages from './harizontal-nav-more-menu.i18n';
import { HorizontalNavLinkItem } from './horizontal-nav-link-item';
import * as styled from './horizontal-nav.style';

const MoreMenu = ({
  menuItems,
  linkComponent,
  intl,
  publishUiEvent,
}: {
  menuItems: MenuItem[];
  linkComponent?: ComponentType<any>;
  intl: InjectedIntl;
  publishUiEvent: (event: AnalyticsEvent) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isHighlighted = menuItems.some(
    item => item.url === window.location.pathname
  );

  const triggerHandler = () => {
    setIsOpen(prev => !prev);
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'navigationItem',
      actionSubjectId: 'moreButton',
      source: 'navigation',
      attributes: {
        itemIsGlobalHorizontalNav: true,
      },
    });
  };

  const renderMenu = useCallback(
    () => (
      <PopupMenuGroup minWidth={149}>
        <Section>
          {menuItems.map(menuItem => (
            <HorizontalNavLinkItem
              linkComponent={linkComponent}
              key={menuItem.url}
              href={menuItem.url}
              isClientLink={menuItem.is_client_link}
              target={menuItem.target}
              analyticsLabel={menuItem.analytics_label}
              publishUiEvent={publishUiEvent}
              isSelected={menuItem.url === window.location.pathname}
            >
              {menuItem.label}
            </HorizontalNavLinkItem>
          ))}
        </Section>
      </PopupMenuGroup>
    ),
    [menuItems, linkComponent, publishUiEvent]
  );

  if (!menuItems.length) {
    return null;
  }

  return (
    <styled.HorizontalNavItem>
      <Popup
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        placement="bottom-start"
        trigger={triggerProps => (
          <InlineDialog
            isOpen={isOpen}
            onClose={() => {}}
            placement="bottom-start"
            content={<div></div>}
          >
            <styled.HorizontalNavItem>
              <PrimaryDropdownButton
                {...triggerProps}
                aria-label={intl.formatMessage(messages.moreButtonAriaLabel)}
                isSelected={isOpen}
                isHighlighted={isHighlighted}
                onClick={triggerHandler}
              >
                <FormattedMessage {...messages.moreButtonLabel} />
              </PrimaryDropdownButton>
            </styled.HorizontalNavItem>
          </InlineDialog>
        )}
        content={renderMenu}
      />
    </styled.HorizontalNavItem>
  );
};

export default React.memo(injectIntl(MoreMenu));
